import * as React from 'react';
import {Button, Table} from 'antd';
import {ColumnProps} from 'antd/lib/table';
import {RouteComponentProps, withRouter} from 'react-router';
import {Link} from 'react-router-dom';

import locales from '../locales';
import {ErrorAlert} from '../common/ErrorAlert';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import * as tableUtils from '../utils/table-utils';
import {GridState, searchableColumn, searchableSortableColumn, sortableColumn} from '../utils/table-utils';
import {ErrorResponse, InventoryTransportLetter, PageResponseEnvelope} from '../api/dtos/dtos';
import furoApi from '../api/FuroApi';
import {formatDateTimeOrNull} from '../utils/date-utils';
import * as routerUtils from '../utils/router-utils';
import * as paths from '../paths';
import ButtonBar from '../common/ButtonBar';
import accountProvider from '../account/AccountProvider';

interface State extends GridState<InventoryTransportLetter> {
  error?: ErrorResponse;
}

class InventoryTransportLettersPage extends React.Component<RouteComponentProps, State> {

  public readonly loadingHandler?: LoadingOverlayHandler;

  public state: State = {
    ...tableUtils.initialGridState(),
    sortColumnKey: 'date',
    sortOrder: 'descend',
    ...this.props.location.state,
  };

  public componentDidMount() {
    this.reload();
  }

  public render() {
    const canCreate = accountProvider.isAdmin();
    const columns: Array<ColumnProps<InventoryTransportLetter>> = [
      searchableSortableColumn(locales.id, 'transportLetterNumber', this.state, this.renderIdColumn),
      searchableColumn(locales.company_name, 'partnerName', this.state, r => r.partner.name),
      searchableSortableColumn(locales.incoming_invoice, 'invoiceNumber', this.state, this.renderIncomingInvoiceLink),
      sortableColumn(locales.date, 'date', this.state, this.renderDate),
      searchableColumn(locales.responsible_user, 'responsibleUser', this.state, r => r.responsibleUser || ''),
    ];

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        {canCreate && (
          <ButtonBar style={{marginBottom: 8}}>
            <Button size="small" type="primary" icon="plus" onClick={this.onCreateClick}>
              {locales.add}
            </Button>
          </ButtonBar>
        )}

        <Table
          dataSource={this.state.data}
          columns={columns}
          rowKey={tableUtils.idRowKeyFn}
          bordered={true}
          size="small"
          pagination={tableUtils.defaultPaginationConfig(this.state)}
          onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
          scroll={{x: 960}}
        />

        <ErrorAlert style={{marginTop: '16px'}} error={this.state.error}/>
      </>
    );
  }

  private renderIdColumn = (record: InventoryTransportLetter): React.ReactNode => {
    const attachmentCount = record.attachmentCount ?? 0;
    return (
      <div className="incoming-invoice-name-container">
        <Link to={paths.viewInventoryTransportLetterPath(record.id)}>{record.transportLetterNumber}</Link>
        {attachmentCount > 0 && <span className="attachments">{attachmentCount} {locales.attachment}</span>}
      </div>
    );
  };

  private renderIncomingInvoiceLink = (record: InventoryTransportLetter): React.ReactNode => {
    const invoice = record.incomingInvoice;

    if (!invoice) {
      return '-';
    }

    return (
      <>
        <Link to={paths.viewIncomingInvoicePath(invoice.id!)}>
          {invoice.invoiceNumber}
        </Link>
        <br/>
        {invoice.name && (
          <span style={{fontSize: '12px'}}>
              {invoice.name}
          </span>
        )}
      </>
    );
  };


  private renderDate = (record: InventoryTransportLetter): React.ReactNode => {
    return formatDateTimeOrNull(record.date) || '-';
  };

  private onCreateClick = () => {
    this.props.history.push(paths.createInventoryTransportLetterPath);
  };

  private onGridChange = (newState: GridState<InventoryTransportLetter>) => {
    this.setState({...newState}, () => this.reload());
  };

  private reload = () => {
    const request = tableUtils.toGridRequest(this.state);
    const promise = furoApi.inventoryTransportLetters.getAll(request)
      .then(this.onReloadSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onReloadSuccess = (response: PageResponseEnvelope<InventoryTransportLetter>) => {
    this.setState({
      ...tableUtils.toGridState(response),
      error: undefined,
    }, () => {
      routerUtils.cacheComponentStateForLocation(this.props, this.state);
    });
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error});
  };
}

export default withRouter(InventoryTransportLettersPage);




// WEBPACK FOOTER //
// ./src/inventory-transport-letters/InventoryTransportLettersPage.tsx